import * as api from 'api';
import { actionTypes } from 'redux-resource';
import { SubmissionError } from 'redux-form';

import { PER_PAGE } from 'helpers/actions';
import { expireTypeToBackendValue, getSubStatus } from 'helpers/subs';
import { getServiceCodeError } from '../helpers/errors';
import { BIG_PAGE_SIZE } from '../constants';

export const fetchAllQuickSubs =
  (page = 1, ordering, filters) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'quickSubs',
      requestKey: `fetchAll${page}`,
      list: `page${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchAllQuickSubs(page, ordering, filters)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const searchSubs =
  (page = 1, search, page_size) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'quickSubs',
      requestKey: `searchSubs${page}`,
      list: `searchPage${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .searchQuickSubs(page, search, page_size)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const fetchUserFilteredSubs =
  (userId, { query, requestKey }, page = 1) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'quickSubs',
      requestKey: `${requestKey}Subs${page}`,
      list: `${requestKey}Subs${page}`,
      mergeListIds: false,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    return api
      .fetchUserQuickFilteredSubs(userId, query, page)
      .then((response) => {
        const { count, next, previous, results } = response.data;
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_SUCCEEDED,
          resources: results || [],
          requestProperties: {
            perPage: (results && results.length) || PER_PAGE,
            count,
            next,
            previous,
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.READ_RESOURCES_FAILED,
        });
      });
  };

export const createSub = (attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'create',
    list: 'userSubs',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.CREATE_RESOURCES_PENDING,
  });

  return api
    .createQuickSub(attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.CREATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchAllQuickInitialsSubs =
  (params, { sort, carriers, expiry_type, status = 'expired' }) =>
  async (dispatch) => {
    const actionDefaults = {
      resourceType: 'quickSubs',
      requestKey: `fetchAllQuickInitialsSubs`,
      list: `allQuickInitialsSubs`,
      mergeListIds: false,
    };

    const apiCalls = {
      att: api.fetchAllAttInitialsSubs,
      quick: api.fetchAllQuickInitialsSubs,
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: [],
    });

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_PENDING,
    });

    let subs = [];

    for (let carrier of carriers) {
      let page = 1;
      let next = 'next-url';

      while (next) {
        let response;

        try {
          response = await apiCalls[carrier](
            page,
            { ...params, [`${carrier}_status`]: status },
            BIG_PAGE_SIZE,
          );
        } catch (e) {
          let messages = e.response
            ? e.response.data
            : { detail: 'Something went wrong' };
          dispatch({
            ...actionDefaults,
            type: actionTypes.READ_RESOURCES_FAILED,
          });
          throw new SubmissionError({
            _error: messages,
          });
        }

        subs = subs.concat(response.data.results);
        next = response.data.next;
        page += 1;
      }
    }

    // sort subs if needed
    subs = typeof sort === 'function' ? sort(subs) : subs;

    // filter out subs with wrong expiry type
    subs = subs.filter(
      (sub) =>
        getSubStatus(sub) !== 'expired' ||
        sub.expiry_type === expireTypeToBackendValue(expiry_type),
    );

    dispatch({
      ...actionDefaults,
      type: actionTypes.READ_RESOURCES_SUCCEEDED,
      resources: subs,
    });

    return subs;
  };

export const findQuickSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'find',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .findQuickSub(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        statusCode: err.response && err.response.status,
      });
    });
};

export const updateQuickSub = (id, sub) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'updateQuickSub',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });
  return api
    .updateQuickSub(id, sub)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateDevice =
  (id, { device_make, ...device }) =>
  (dispatch) => {
    const actionDefaults = {
      resourceType: 'quickSubs',
      requestKey: 'updateDevice',
      resources: [id],
    };

    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_PENDING,
    });

    return api
      .updateQuickDevice(id, device)
      .then((response) => {
        if (response.data.valid === false) {
          throw new Error(response);
        }
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
          resources: [id],
          requestProperties: {
            details: {
              valid: response.data.valid,
              ...response.data.details,
            },
          },
        });
      })
      .catch((err) => {
        dispatch({
          ...actionDefaults,
          type: actionTypes.UPDATE_RESOURCES_FAILED,
        });

        let messages = err.response
          ? err.response.data
          : { detail: 'Something went wrong' };
        throw new SubmissionError(messages);
      });
  };

export const suspendQuickSub = (subId, data) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'suspendQuickSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  const suspensionStatus = data.suspension_status;
  const expireType =
    suspensionStatus === 'expiry_parking' ? data.expiry_type : undefined;

  return api
    .suspendQuickSubscription(subId, suspensionStatus, expireType)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const restoreQuickSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'restoreQuickSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .restoreQuickSubscription(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail || messages.error) {
        throw new SubmissionError({ _error: messages });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const reactivateQuickSub = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'reactivateQuickSub',
  }

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api.reactivateQuickSubscription(subId).then(response => {
    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
      resources: [response.data],
    });
  }).catch(err => {
    dispatch({
      ...actionDefaults,
      type: actionTypes.UPDATE_RESOURCES_FAILED,
    });

    let messages = err.response ? err.response.data : { detail: 'Something went wrong' }
    if (messages.detail || messages.error) {
      throw new SubmissionError({ _error: messages });
    } else {
      throw new SubmissionError(messages);
    }
  });
}


export const expireQuickSubscription = (subId, values) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'expireSub',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .expireQuickSubscription(subId, values)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updatePlan = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'updatePlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .updateQuickPlan(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      if (messages.detail) {
        throw new SubmissionError({ _error: messages });
      } else if (
        messages.message ||
        messages.error ||
        messages.quick_subscription
      ) {
        throw new SubmissionError({
          _error:
            messages.message || messages.error || messages.quick_subscription,
        });
      } else {
        throw new SubmissionError(messages);
      }
    });
};

export const lockQuickPlan = (subId, locked) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'lockQuickPlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .lockQuickPlan(subId, locked)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchUpcomingPlan = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'fetchUpcomingPlan',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchQuickSubUpcomingPlan(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          upcomingPlan: response.data.results,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const setUpcomingQuickPlan = (subId, plan) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'setUpcomingQuickPlan',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .setUpcomingQuickPlan(subId, plan)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const quickChangeVoicemailPin = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'quickChangeVoicemailPin',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });
  return api
    .quickChangeVoicemailPin(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const quickDeleteVoicemailPin = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'quickDeleteVoicemailPin',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });
  return api
    .quickDeleteVoicemailPin(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const changeQuickCallerId = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'changeQuickCallerId',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });
  return api
    .changeQuickCallerId(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const changeQuickMdn = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'changeQuickMdn',
    resources: [id],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });
  return api
    .changeQuickMdn(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
      return response.data;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const activateSub = (subId, activation) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'activateSub',
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .activateQuickSub(subId, activation)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        meta: {
          activation: {
            ...response.data,
          },
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const sendQuickVerificationCode = (id, attrs) => (dispatch) => {
  return api
    .sendQuickVerificationCode(id, attrs)
    .then((response) => {
      if (response.data.success === false) {
        const err = { response };
        throw err;
      }

      return {
        success: true,
      };
    })
    .catch((err) => {
      let errors = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: errors,
      });
    });
};

export const verifyQuickVerificationCode = (id, attrs) => (dispatch) => {
  return api
    .verifyQuickVerificationCode(id, attrs)
    .then((response) => {
      if (response.data.success === false) {
        const err = { response };
        throw err;
      }

      return {
        success: true,
      };
    })
    .catch((err) => {
      let errors = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError({
        _error: errors,
      });
    });
};

export const quickSubmitPort = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: `quickSubmitPort${subId}`,
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .quickSubmitPort(subId, port)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const quickInternalPort = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: `quickInternalPort${subId}`,
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .quickInternalPort(subId, port)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const updateQuickPort = (subId, port) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: `updatePort${subId}`,
    resources: [subId],
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .updateQuickPort(subId, port)
    .then((response) => {
      const { subscription, ...requestProperties } = response.data;
      const subMeta = {};
      subMeta[subId] = {
        portInfo: requestProperties.port_info,
      };
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        requestProperties,
      });
      dispatch({
        type: 'UPDATE_RESOURCES',
        meta: {
          quickSubs: subMeta,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
        requestProperties: err.response && err.response.data,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const portCancel = (subId, cancel) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'portCancel',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .cancelQuickPort(subId, cancel)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        resources: [response.data.subscription],
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchServiceCodes = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'all',
    resources: [id],
    meta: {
      serviceCodesError: '',
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchQuickServiceCodes(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        resourceType: 'quickSubs',
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          serviceCodes: response.data.service_codes,
        },
      });
    })
    .catch((error) => {
      const errMessage = getServiceCodeError(error);
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
        meta: {
          serviceCodesError: errMessage,
        },
      });
    });
};

export const updateQuickServiceCodes = (id, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'updateQuickServiceCodes',
    resources: [id],
    meta: {
      serviceCodesError: '',
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .updateQuickServiceCodes(id, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        resourceType: 'quickSubs',
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        resources: [id],
        meta: {
          serviceCodes: response.data.service_codes,
        },
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchQuickRunningActions = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'fetchQuickRunningActions',
    resources: [id],
    meta: {
      runningActions: [],
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchQuickRunningActions(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          runningActions: response.data || [],
        },
      });
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const quickSetServiceAddress = (subId, attrs) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'portCancel',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
  });

  return api
    .quickSetServiceAddress(subId, attrs)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
      });
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};

export const fetchQuickPortData = (id, params) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'fetchQuickPortData',
    resources: [id],
    meta: {
      portData: null,
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchQuickPortData(id, params)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          portData: response.data || null,
        },
      });
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const fetchQuickMdnHistory = (id) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    requestKey: 'fetchQuickMdnHistory',
    resources: [id],
    meta: {
      mdnHistory: [],
    },
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.READ_RESOURCES_PENDING,
  });

  return api
    .fetchQuickMdnHistory(id)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_SUCCEEDED,
        meta: {
          mdnHistory: response.data || [],
        },
      });
    })
    .catch((error) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.READ_RESOURCES_FAILED,
      });
    });
};

export const quickUpdateSubStatus = (subId) => (dispatch) => {
  const actionDefaults = {
    resourceType: 'quickSubs',
    resources: [subId],
    requestKey: 'quickUpdateSubStatus',
  };

  dispatch({
    ...actionDefaults,
    type: actionTypes.UPDATE_RESOURCES_PENDING,
    meta: {
      syncStatus: null,
    },
  });

  return api
    .quickUpdateSubStatus(subId)
    .then((response) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_SUCCEEDED,
        meta: {
          syncStatus: response.data || null,
        },
      });
      return response;
    })
    .catch((err) => {
      dispatch({
        ...actionDefaults,
        type: actionTypes.UPDATE_RESOURCES_FAILED,
      });

      let messages = err.response
        ? err.response.data
        : { detail: 'Something went wrong' };
      throw new SubmissionError(messages);
    });
};
